/* Urban Customz — homepage product scene.
   One continuous scene of real product blanks. Scrolling a short sticky track
   prints the selected artwork onto each product with overlapping timing, then
   cross-fades the arrival copy to the selector copy and raises the selector.

   Feel: the scroll position is followed through a short exponential lag (about
   90 ms), the way scroll-driven product pages glide instead of snapping to the
   wheel. Every window is eased in CSS. Scrolling back runs the same curve in
   reverse; the design the visitor picked stays picked.

   Cost control: a frame writes ONE custom property (--p) while the scene is
   pinned, and one more (--x) while it leaves. Every other animated value — each
   product's reveal, the copy cross-fade, the selector rise, the exit drift — is
   derived from them in CSS.

   Exit: from the moment the stage unpins, EXIT_PAST of its height of ordinary
   scroll is one window, --x 0..1 with the same lag. Over it the products fly
   out of the frame in every direction with gathering speed, growing as they
   go — no fade — while the next section rises under them. No scroll distance
   is added; scrolling back brings them home.

   Progressive enhancement: without this script — or with reduced motion — the
   scene renders finished: artwork printed under the arrival headline, selector
   visible, every link usable. */
(() => {
  if (window.__ucSceneReady) return;
  window.__ucSceneReady = true;

  const UI_AT = 0.66; // progress at which the selector becomes usable; matches the CSS fade
  const COPY_AT = 0.62; // progress at which the arrival copy has fully faded and the selector copy takes over
  const LAG_MS = 90; // time constant of the scroll follow
  // The windows in the CSS are written against --q = --p * END, so the last
  // one (the selector, 0.66-0.82) completes exactly as the stage unpins and
  // there is no dead hold before the next section rises.
  const END = 0.82;
  const EXIT_AT = 1; // the exit begins the moment the stage unpins
  const EXIT_PAST = 0.85; // how much of the stage's height the fly-out runs while it scrolls away
  const GONE_AT = 0.9; // exit progress past which the thumbnails are hidden from pointer and focus
  const scenes = new Set();
  let ticking = false;

  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const idle = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 4000 }) : setTimeout(fn, 1500));

  class Scene {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-uc-track]');
      this.stage = root.querySelector('[data-uc-stage]');
      this.prods = Array.from(root.querySelectorAll('[data-uc-prod]'));
      this.ui = Array.from(root.querySelectorAll('[data-uc-ui]'));
      this.copyA = root.querySelector('[data-uc-copy="a"]');
      this.copyB = root.querySelector('[data-uc-copy="b"]');
      this.arts = Array.from(root.querySelectorAll('[data-art-index]'));
      // Scoped to the two buttons: the product figures carry data-family too.
      this.families = Array.from(root.querySelectorAll('.uc-seg__btn[data-family]'));
      this.cta = root.querySelector('[data-uc-cta]');
      this.ctaLabel = root.querySelector('[data-uc-cta-label]');
      this.forced = false;
      this.p = -1;
      this.cur = 0;
      this.target = 0;
      this.x = 0;
      this.xcur = 0;
      this.xt = 0;
      this.running = false;
      this.head = 0;
      this.onFocus = this.onFocus.bind(this);
      this.tick = this.tick.bind(this);
      this.bind();
      this.syncHead();
      // The header changes height on its own schedule (utility bar collapse);
      // follow it the moment it does, not on the next scroll event.
      const wrapper = document.querySelector('sticky-header, .uc-header-wrapper');
      if (wrapper && 'ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.syncHead());
        this.ro.observe(wrapper);
      }
      this.isStatic = root.dataset.animate === 'false' || reduced();
      // Static = the finished composition under the arrival headline.
      if (this.isStatic) this.complete();
      else {
        root.classList.add('is-live');
        this.update(true);
      }
    }

    bind() {
      this.arts.forEach((btn) => {
        const index = Number(btn.dataset.artIndex);
        btn.addEventListener('click', () => this.pickArt(index));
        btn.addEventListener('keydown', (e) => this.roving(e, this.arts, (i) => this.pickArt(i)));
        // Fetch as soon as someone shows interest, so the tap waits on nothing.
        ['pointerenter', 'touchstart', 'focus'].forEach((type) => {
          btn.addEventListener(type, () => this.warm(index), { passive: true });
        });
      });
      this.families.forEach((btn) => {
        btn.addEventListener('click', () => this.pickFamily(btn.dataset.family));
        btn.addEventListener('keydown', (e) =>
          this.roving(e, this.families, (i) => this.pickFamily(this.families[i].dataset.family))
        );
      });
      // A keyboard visitor tabbing in gets the finished state at once, so nothing
      // is ever focusable while it is still invisible.
      this.root.addEventListener('focusin', this.onFocus);
    }

    roving(e, list, pick) {
      const i = list.indexOf(e.currentTarget);
      let next = -1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % list.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + list.length) % list.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = list.length - 1;
      else if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        pick(i);
        return;
      }
      if (next < 0) return;
      e.preventDefault();
      list[next].focus();
      pick(next);
    }

    onFocus() {
      if (this.isStatic || this.forced) return;
      this.forced = true;
      this.complete();
    }

    // Sources for one design: the printed twin per product role, read off its button.
    sources(index) {
      const btn = this.arts[index];
      if (!btn) return [];
      return this.prods.map((fig) => {
        const role = fig.dataset.role;
        const key = `print${role[0].toUpperCase()}${role.slice(1)}`;
        return { fig, src: btn.dataset[key] || '', srcset: btn.dataset[`${key}Set`] || '' };
      });
    }

    // Fetch and decode a design off-screen, so swapping it in costs nothing.
    warm(index) {
      if (!this.preloads) this.preloads = [];
      if (this.preloads[index]) return this.preloads[index];
      const jobs = this.sources(index).filter((s) => s.src).map((s) => {
        const img = new Image();
        const shown = s.fig.querySelector('.uc-prod__printed');
        img.sizes = shown ? shown.sizes : '';
        if (s.srcset) img.srcset = s.srcset;
        img.src = s.src;
        return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
      });
      this.preloads[index] = Promise.all(jobs);
      return this.preloads[index];
    }

    warmAll() {
      if (this.warmed) return;
      this.warmed = true;
      this.arts.forEach((btn, i) => this.warm(i));
    }

    pickArt(index) {
      const btn = this.arts[index];
      if (!btn) return;
      // Button state first, so the control answers immediately on a slow phone.
      this.arts.forEach((b, i) => {
        const on = i === index;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      this.warm(index).then(() => {
        this.root.dataset.art = String(index);
        this.sources(index).forEach((s) => {
          const img = s.fig.querySelector('.uc-prod__printed');
          if (!img || !s.src) return;
          img.dataset.art = String(index);
          if (s.srcset) img.srcset = s.srcset;
          img.src = s.src;
        });
      });
    }

    pickFamily(family) {
      this.root.dataset.family = family;
      // Until someone chooses, every product stays at full strength.
      this.root.dataset.picked = 'true';
      this.families.forEach((btn) => {
        const on = btn.dataset.family === family;
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
        btn.tabIndex = on ? 0 : -1;
      });
      const btn = this.families.find((b) => b.dataset.family === family);
      if (btn && this.cta) {
        if (btn.dataset.url) this.cta.href = btn.dataset.url;
        if (this.ctaLabel && btn.dataset.cta) this.ctaLabel.textContent = btn.dataset.cta;
      }
    }

    // Track geometry is cached so a scroll frame reads scrollY and writes one
    // property, with no forced layout. It is refreshed on resize, when the header
    // changes height, when the page finishes loading, and every second while the
    // visitor scrolls, in case something above the scene changed size.
    measure() {
      if (!this.track || !this.stage) return;
      const rect = this.track.getBoundingClientRect();
      this.trackTop = rect.top + window.scrollY;
      this.travel = rect.height - this.stage.offsetHeight - (this.fixed || this.head);
      // The exit window: the tail of the pinned travel plus part of the stage
      // scrolling out of view.
      const travel = Math.max(0, this.travel);
      this.exitStart = this.trackTop + EXIT_AT * travel;
      this.exitLen = Math.max(240, Math.round((1 - EXIT_AT) * travel + this.stage.offsetHeight * EXIT_PAST));
      this.measuredAt = performance.now();
    }

    // 0 until the exit window opens, 1 at its end. Reads the same cached
    // geometry as progress().
    exit() {
      if (!this.track || !this.stage) return 0;
      if (!this.exitLen || performance.now() - this.measuredAt > 1000) this.measure();
      return clamp01((window.scrollY - this.exitStart) / (this.exitLen || 1));
    }

    progress() {
      if (!this.track || !this.stage) return 1;
      if (!this.travel || performance.now() - this.measuredAt > 1000) this.measure();
      if (this.travel <= 0) return 1;
      return clamp01((window.scrollY - this.trackTop) / this.travel);
    }

    // The theme's header is sticky and loses its utility bar on scroll. The stage
    // pins under whatever height the header has right now (--scene-head), but it
    // is SIZED to the header's shrunk height (--scene-fixed), so the products never
    // resize while the scene is pinned.
    syncHead() {
      const wrapper = document.querySelector('sticky-header, .uc-header-wrapper');
      if (!wrapper) return;
      const h = Math.round(wrapper.getBoundingClientRect().height);
      let folds = 0;
      wrapper.querySelectorAll('.uc-utility, .uc-mcontact').forEach((el) => { folds += el.getBoundingClientRect().height; });
      const fixed = Math.round(h - folds);
      if (h && Math.abs(h - this.head) > 1) {
        this.head = h;
        this.root.style.setProperty('--scene-head', `${h}px`);
      }
      if (fixed && fixed !== this.fixed) {
        this.fixed = fixed;
        this.root.style.setProperty('--scene-fixed', `${fixed}px`);
      }
      this.measure();
    }

    // Finished composition under the ARRIVAL headline, selector usable. Used for
    // reduced motion, the animation switched off, keyboard entry and the editor.
    complete() {
      this.running = false;
      this.root.classList.add('is-complete');
      const st = this.root.style;
      st.setProperty('--p', '1');
      st.setProperty('--rev', '1');
      st.setProperty('--ui', '1');
      st.setProperty('--ca', '0');
      st.setProperty('--cb', '0');
      st.setProperty('--hint', '1');
      st.setProperty('--x', '0');
      this.x = 0;
      this.xcur = 0;
      this.xt = 0;
      this.wasGone = false;
      this.root.classList.remove('is-gone');
      this.prods.forEach((el) => el.style.setProperty('--rev', '1'));
      this.setCopy(false);
      this.setUi(true);
    }

    setUi(on) {
      if (this.uiShown === on) return;
      this.uiShown = on;
      this.ui.forEach((el) => {
        el.classList.toggle('is-on', on);
        el.toggleAttribute('inert', !on);
      });
      // Once the selector is in front of the visitor, quietly fetch the other
      // designs so the first switch has nothing to wait for.
      if (on) idle(() => this.warmAll());
    }

    // Which copy is the real one for assistive tech: the arrival headline with
    // its buttons, or the selector headline. Only ever flipped once the outgoing
    // copy is fully transparent, so nothing pops.
    setCopy(b) {
      if (this.copyShown === b) return;
      this.copyShown = b;
      this.root.classList.toggle('is-b', b);
      if (this.copyA) {
        this.copyA.toggleAttribute('inert', b);
        this.copyA.setAttribute('aria-hidden', b ? 'true' : 'false');
      }
      if (this.copyB) this.copyB.setAttribute('aria-hidden', b ? 'false' : 'true');
    }

    // Scroll → target; the rendered value follows it with a short lag. On a
    // device that cannot paint the glide at frame rate, the lag is dropped and
    // the scene simply tracks the scroll position, which costs one frame per
    // scroll event instead of one per animation frame.
    update(force) {
      if (this.isStatic) return;
      this.xt = this.exit();
      if (this.forced) {
        // Finished composition (keyboard entry, editor): still leaves gracefully.
        this.xcur = this.xt;
        this.renderExit();
        return;
      }
      this.target = this.progress();
      if (force || this.direct) {
        this.cur = this.target;
        this.xcur = this.xt;
        this.render();
        return;
      }
      if (!this.running) {
        this.running = true;
        this.last = performance.now();
        requestAnimationFrame(this.tick);
      }
    }

    tick(now) {
      if (!this.running) return;
      const raw = now - (this.last || now);
      const dt = Math.min(64, raw);
      this.last = now;
      // Three long frames in a row while gliding: this device is better served
      // by direct tracking for the rest of the visit.
      this.slow = raw > 40 ? (this.slow || 0) + 1 : Math.max(0, (this.slow || 0) - 1);
      if (this.slow >= 3) {
        this.direct = true;
        this.cur = this.target;
        this.xcur = this.xt;
        this.render();
        this.running = false;
        return;
      }
      const d = this.target - this.cur;
      const dx = this.xt - this.xcur;
      if (Math.abs(d) < 0.0006 && Math.abs(dx) < 0.0006) {
        this.cur = this.target;
        this.xcur = this.xt;
        this.render();
        this.running = false;
        return;
      }
      const k = 1 - Math.exp(-dt / LAG_MS);
      this.cur += d * k;
      this.xcur += dx * k;
      this.render();
      requestAnimationFrame(this.tick);
    }

    renderExit() {
      const x = this.xcur;
      if (Math.abs(x - this.x) < 0.0005) return;
      this.x = x;
      this.root.style.setProperty('--x', x.toFixed(4));
      const gone = x >= GONE_AT;
      if (this.wasGone !== gone) {
        this.wasGone = gone;
        this.root.classList.toggle('is-gone', gone);
      }
    }

    render() {
      this.renderExit();
      const p = this.cur;
      if (Math.abs(p - this.p) < 0.0003) return;
      this.p = p;
      this.root.style.setProperty('--p', p.toFixed(4));
      const q = p * END;
      this.setCopy(q >= COPY_AT);
      this.setUi(q >= UI_AT);
      const done = p > 0.98;
      if (this.wasComplete !== done) {
        this.wasComplete = done;
        this.root.classList.toggle('is-complete', done);
      }
    }

    destroy() {
      this.running = false;
      if (this.ro) this.ro.disconnect();
      this.root.removeEventListener('focusin', this.onFocus);
      delete this.root.dataset.ucMounted;
      scenes.delete(this);
    }
  }

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      scenes.forEach((s) => s.update(false));
    });
  };

  const onResize = () => {
    scenes.forEach((s) => {
      s.syncHead();
      s.measure();
      s.update(true);
    });
  };
  window.addEventListener('load', onResize);

  const mount = (root) => {
    if (root.dataset.ucMounted) return;
    root.dataset.ucMounted = '1';
    const scene = new Scene(root);
    scenes.add(scene);
    root.__ucScene = scene;
  };

  const init = (scope) => (scope || document).querySelectorAll('[data-uc-scene]').forEach(mount);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => {
    scenes.forEach((s) => {
      s.isStatic = s.root.dataset.animate === 'false' || reduced();
      if (s.isStatic) s.complete();
    });
  });

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-uc-scene]').forEach((root) => {
      if (root.__ucScene) root.__ucScene.destroy();
    });
  });
  document.addEventListener('shopify:section:select', (e) => {
    e.target.querySelectorAll('[data-uc-scene]').forEach((root) => {
      if (root.__ucScene) {
        root.__ucScene.forced = true;
        root.__ucScene.complete();
      }
    });
  });
})();
